import { DeepWritable, noop } from 'ts-essentials';

import { api } from 'app/app.api';

import { PaymentInformation } from './types/billing.types';
import {
  CreateCreditCardApiArg,
  CreateCreditCardApiResponse,
  CreateSetupIntentApiResponse,
  DeletePaymentMethodApiArg,
  DeletePaymentMethodApiResponse,
  PaymentInformationPatchAttributes,
  ReadInvoiceApiArg,
  ReadInvoiceApiResponse,
  ReadInvoicesApiResponse,
  ReadPaymentInformationApiResponse,
  ReadPaymentMethodsApiResponse,
  ReadValidateVatNumberApiArg,
  ReadValidateVatNumberApiResponse,
  UpdateDefaultPaymentMethodApiArg,
  UpdateDefaultPaymentMethodApiResponse,
  UpdatePaymentInformationApiArg,
  UpdatePaymentInformationApiResponse,
} from './types/billingApi.types';

export const billingApi = api.injectEndpoints({
  endpoints: (builder) => ({
    readPaymentInformation: builder.query<
      ReadPaymentInformationApiResponse,
      void
    >({
      query: () => ({
        version: 1,
        path: 'billing/paymentinformation',
      }),
    }),
    updatePaymentInformation: builder.mutation<
      UpdatePaymentInformationApiResponse,
      UpdatePaymentInformationApiArg
    >({
      query: ({ patch: body }) => ({
        version: 1,
        method: 'PATCH',
        path: 'billing/paymentinformation',
        body,
      }),
      async onQueryStarted({ patch }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (Object.keys(data.data.update_errors).length !== 0) {
            return;
          }

          dispatch(
            billingApi.util.updateQueryData(
              'readPaymentInformation',
              undefined,
              (draft) => {
                const mutableDraft =
                  draft as DeepWritable<ReadPaymentInformationApiResponse>;

                for (const [key, value] of Object.entries(
                  (patch as { updates?: PaymentInformation }).updates ?? {},
                )) {
                  mutableDraft.data.payment_information[key] = value;
                }

                (
                  patch as { deletes?: PaymentInformationPatchAttributes[] }
                ).deletes?.forEach((key) => {
                  mutableDraft.data.payment_information[key] = '';
                });
              },
            ),
          );
        } catch {
          noop();
        }
      },
    }),
    readValidateVatNumber: builder.query<
      ReadValidateVatNumberApiResponse,
      ReadValidateVatNumberApiArg
    >({
      query: ({ vatNumber }) => ({
        version: 1,
        path: `billing/paymentinformation/vatin/${vatNumber}/validate`,
      }),
    }),
    readPaymentMethods: builder.query<ReadPaymentMethodsApiResponse, void>({
      query: () => ({
        version: 1,
        path: 'billing/paymentmethod',
      }),
    }),
    updateDefaultPaymentMethod: builder.mutation<
      UpdateDefaultPaymentMethodApiResponse,
      UpdateDefaultPaymentMethodApiArg
    >({
      query: ({ id }) => ({
        version: 1,
        method: 'PUT',
        path: `billing/paymentmethod/${id}/default`,
      }),
    }),
    deletePaymentMethod: builder.mutation<
      DeletePaymentMethodApiResponse,
      DeletePaymentMethodApiArg
    >({
      query: ({ id }) => ({
        version: 1,
        method: 'DELETE',
        path: `billing/paymentmethod/${id}`,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patchReadPaymentMethods = dispatch(
          billingApi.util.updateQueryData(
            'readPaymentMethods',
            undefined,
            (draft) => {
              const mutablePaymentMethods = draft.data
                .payment_methods as DeepWritable<
                typeof draft.data.payment_methods
              >;

              const index = draft.data.payment_methods.findIndex(
                (_paymentMethod) => _paymentMethod.id === id,
              );

              if (index !== -1) {
                mutablePaymentMethods.splice(index, 1);
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchReadPaymentMethods.undo();
        }
      },
    }),
    createCreditCard: builder.mutation<
      CreateCreditCardApiResponse,
      CreateCreditCardApiArg
    >({
      query: ({ token }) => ({
        version: 1,
        method: 'POST',
        path: 'billing/creditcard',
        body: { token },
      }),
    }),
    readInvoices: builder.query<ReadInvoicesApiResponse, void>({
      query: () => ({
        version: 1,
        path: 'billing/invoices',
      }),
    }),
    readInvoice: builder.query<ReadInvoiceApiResponse, ReadInvoiceApiArg>({
      query: ({ id }) => ({
        version: 1,
        path: `billing/invoices/${id}/pdf`,
      }),
    }),
    createSetupIntent: builder.mutation<CreateSetupIntentApiResponse, void>({
      query: () => ({
        version: 1,
        path: `billing/setupintent`,
        method: 'POST',
      }),
    }),
  }),
});
